import { useEffect } from "react";
import SEO_EN from "../content/seo.json";
import SEO_DE from "../content/seo.de.json";
import SEO_FR from "../content/seo.fr.json";
import SEO_AR from "../content/seo.ar.json";
import GUIDES_JSON from "../content/guides.json";
import { LANGUAGES } from "../i18n/languages";
import { withLang } from "../i18n/paths";

// Page titles, descriptions and links for search engines and share previews.
// The same data (src/content/seo*.json, one file per language) is baked into
// static HTML at build time by scripts/prerender.js, so crawlers see it
// without running JavaScript. Game page addresses (slugs) are the same in
// every language: /games/mafia, /de/games/mafia, /ar/games/mafia.
const BY_LANG = { en: SEO_EN, de: SEO_DE, fr: SEO_FR, ar: SEO_AR };
export const SITE = SEO_EN.site;
export const GAME_PAGES = SEO_EN.games;
export const gamePath = (id, lang = "en") => withLang(lang, `/games/${GAME_PAGES[id].slug}`);
export const gameBySlug = (slug) => Object.keys(GAME_PAGES).find((id) => GAME_PAGES[id].slug === slug) || null;

// Written guides (/guides/…): English only, with links to the games they mention.
export const GUIDES = GUIDES_JSON.guides;
export const guideBySlug = (slug) => GUIDES.find((g) => g.slug === slug) || null;

// A page's or game's text in a language (English where it's missing).
export const pageSeo = (lang, route) => ({ ...SEO_EN.pages[route], ...BY_LANG[lang]?.pages?.[route] });
export const gameSeo = (lang, id) => ({ ...SEO_EN.games[id], ...BY_LANG[lang]?.games?.[id] });

// Finds (or adds) the head tag matching `attrs` and sets `key` to `value`;
// a null value removes the tag.
function setTag(tag, attrs, key, value) {
  const selector = tag + Object.entries(attrs).map(([k, v]) => `[${k}="${v}"]`).join("");
  let el = document.head.querySelector(selector);
  if (value == null) return el?.remove();
  if (!el) {
    el = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute(key, value);
}

// Only the fields given are changed. `path` (without the language part) sets
// the canonical address and the links to the page in the other languages;
// `noindex` keeps a page (a game room, a 404) out of search results.
export function useSeo({ title, description, path, lang = "en", noindex = false }) {
  useEffect(() => {
    if (title) {
      document.title = title;
      setTag("meta", { property: "og:title" }, "content", title);
    }
    if (description) {
      setTag("meta", { name: "description" }, "content", description);
      setTag("meta", { property: "og:description" }, "content", description);
    }
    if (path) {
      setTag("link", { rel: "canonical" }, "href", SITE.url + withLang(lang, path));
      setTag("meta", { property: "og:url" }, "content", SITE.url + withLang(lang, path));
    }
    const alternates = path && !noindex;
    for (const l of LANGUAGES) setTag("link", { rel: "alternate", hreflang: l.code }, "href", alternates ? SITE.url + withLang(l.code, path) : null);
    setTag("link", { rel: "alternate", hreflang: "x-default" }, "href", alternates ? SITE.url + path : null);
    setTag("meta", { name: "robots" }, "content", noindex ? "noindex" : null);
  }, [title, description, path, lang, noindex]);
}
